import React, { useEffect, useState } from 'react';
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Form, Spin, Input, Radio, Space, Button as AntdButton, Avatar, Steps } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { getContent, toggleSpinner, editContent, setContent, getOpportunity, getProgramById } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import Button from "../../components/Button";
import Select from "../../components/CustomSelect";
import { getLocalStorageItem, numericValidator } from "../../utils/utilities";
import {
    toggleSpinnerSelector,
    opportunityDataSelector,
    savedContentListSelector,
    savedContentDataSelector,
} from "../../redux/selectors";
import HorizontalGraph from "../../components/HorizontalGraph";
import environmentIcon from "../../assets/environmentIcon.svg";
import socialIcon from "../../assets/socialIcon.svg";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
};

const beneficiariesList = [
    'Older People',
    'Younger People',
    'Children and Babies',
    'Females',
    'Males',
    'Learning disabilities',
    'Physical disabilities',
    'Mental health challenges',
    'Physical health challenges',
    'Particular ethnic or racial origin',
    'Migrants',
    'LBGTQ+',
    'Educationally or economically disadvantaged',
    'Long-term unemployed',
    'Ex-homeless',
    'Ex-offenders',
    'Ex-service personnel',
    'Academic Institutions',
    'Other charities or voluntary bodies',
    'The General Public/Mankind',
    'Wildlife',
    'Other'
];

const CharityInterimCreateReport = ({ className }) => {
    const baseClassName = clsx("charityInterimCreateReport", className);
    const intl = useIntl();
    const [formData, setFormData] = useState({});
    const [formListError, setFormListError] = useState(null);
    const [savedClicked, setSavedClicked] = useState(false);
    const opportunityData = useAppSelector(opportunityDataSelector) || {}
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const showSpinner = useAppSelector(toggleSpinnerSelector);
    const { ID } = useParams();
    const { ID: contentId,
        beneficiariesChanged,
        beneficiariesNumberChange,
        environmentMeasuresChange,
        totalDirect: savedTotalDirect,
        totalIndirect: savedTotalIndirect,
        actualEnvironmentalMeasure: savedActualEnvironmentMeasure,
        beneficiariesType: savedBeneficiariesType,
    } = useAppSelector(savedContentDataSelector) || {}
    const { opportunityID } = JSON.parse(getLocalStorageItem('contentData') || '{}');
    const { opportunity:
        { programID,
            environmentMeasure,
            opportunityBeneficiaries,
            environmentalMeasure,
            directBeneficiaries,
            indirectBeneficiaries,
        } = {} }
        = opportunityData || {};
    const { message } = useAppSelector(savedContentListSelector) || {};

    const categories = [
        {
            category: intl.formatMessage({ id: 'habitats' }),
            subCategory: [
                intl.formatMessage({ id: 'forest' }),
                intl.formatMessage({ id: 'grassland' }),
                intl.formatMessage({ id: 'deserts' }),
                intl.formatMessage({ id: 'mountains' }),
                intl.formatMessage({ id: 'coastal' }),
                intl.formatMessage({ id: 'marine' }),
                intl.formatMessage({ id: 'freshwater' }),
                intl.formatMessage({ id: 'polar' }),
                intl.formatMessage({ id: 'wetlands' }),
                intl.formatMessage({ id: 'rainforest' }),
                intl.formatMessage({ id: 'arctic' }),
                intl.formatMessage({ id: 'tundra' }),
            ],
            actions: [
                intl.formatMessage({ id: 'restored' }),
                intl.formatMessage({ id: 'protected' }),
                intl.formatMessage({ id: 'created' }),
            ],
            units: [
                intl.formatMessage({ id: 'number' }),
                intl.formatMessage({ id: 'hectares' }),
            ],
        },
        {
            category: intl.formatMessage({ id: 'species' }),
            subCategory: [
                intl.formatMessage({ id: 'terrestrial_animals' }),
                intl.formatMessage({ id: 'aquatic_animals' }),
                intl.formatMessage({ id: 'terrestrial_plants' }),
                intl.formatMessage({ id: 'aquatic_plants' }),
            ],
            actions: [
                intl.formatMessage({ id: 'protected' }),
                intl.formatMessage({ id: 'established' }),
                intl.formatMessage({ id: 'benefitted' }),
                intl.formatMessage({ id: 'saved' }),
            ],
            units: [intl.formatMessage({ id: 'number' })],
        },
        {
            category: intl.formatMessage({ id: 'emissions' }),
            subCategory: [
                intl.formatMessage({ id: 'greenhouse_gases' }),
            ],
            actions: [
                intl.formatMessage({ id: 'reduced' }),
                intl.formatMessage({ id: 'captured' }),
                intl.formatMessage({ id: 'prevented' }),
            ],
            units: [
                intl.formatMessage({ id: 'tonnes_of_CO2_equivalents_C02e' }),
            ],
        },
    ];

    useEffect(() => {
        const validateFormList = () => {
            if (formData?.environmentMeasuresChange === "Y" && !formData?.actualEnvironmentalMeasure?.length) {
                setFormListError('Please enter at least 1 row');
            } else {
                setFormListError(null);
            }
        };

        validateFormList();
    }, [form, formData]);

    useEffect(() => {
        if (contentId) {
            const data = {
                ...(savedTotalDirect && { totalDirect: savedTotalDirect }),
                ...(savedTotalIndirect && { totalIndirect: savedTotalIndirect }),
                ...(savedActualEnvironmentMeasure && { actualEnvironmentalMeasure: savedActualEnvironmentMeasure }),
                ...(savedBeneficiariesType && { beneficiariesType: savedBeneficiariesType }),
                ...(beneficiariesChanged === 'Y' && { beneficiariesChanged: 'Y' }),
                ...(beneficiariesChanged === 'N' && { beneficiariesChanged: 'N' }),
                ...(beneficiariesNumberChange === 'Y' && { beneficiariesNumberChange: 'Y' }),
                ...(beneficiariesNumberChange === 'N' && { beneficiariesNumberChange: 'N' }),
                ...(environmentMeasuresChange === 'Y' && { environmentMeasuresChange: 'Y' }),
                ...(environmentMeasuresChange === 'N' && { environmentMeasuresChange: 'N' }),
            };
            form.setFieldsValue(data);
            setFormData(data);
        }
    }, [contentId]);

    useEffect(() => {
        if (message && !savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content/final/objectives/${ID}`);
        } else if (message && savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content`);
        }
    }, [message])

    useEffect(() => {
        if (opportunityID) {
            document?.documentElement?.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getContent({ contentID: ID }));
            dispatch(getOpportunity(opportunityID));
        }
    }, [opportunityID]);

    useEffect(() => {
        if (programID) {
            dispatch(getProgramById(programID));
        }
    }, [programID]);

    const onFinishFailed = (e) => {
        console.log("Failed:", e);
    };

    const handleProceed = (e) => {
        form.validateFields(['beneficiariesChanged', 'beneficiariesNumberChange', 'environmentMeasuresChange']).then(values => {
            const { beneficiariesChanged: formBeneficiariesChanged, beneficiariesNumberChange: formBeneficiariesNumberChange, environmentMeasuresChange: formEnvironmentMeasuresChange } = formData || {};
            if (formBeneficiariesChanged === undefined) {
                form.setFields([
                    {
                        name: 'beneficiariesChanged',
                        errors: [intl.formatMessage({ id: 'please_select' })],
                    },
                ]);
                return
            }
            if (formBeneficiariesNumberChange === undefined) {
                form.setFields([
                    {
                        name: 'beneficiariesNumberChange',
                        errors: [intl.formatMessage({ id: 'please_select' })],
                    },
                ]);
                return
            }
            if (formEnvironmentMeasuresChange === undefined) {
                form.setFields([
                    {
                        name: 'environmentMeasuresChange',
                        errors: [intl.formatMessage({ id: 'please_select' })],
                    },
                ]);
                return
            }
            if ((formListError) && formData?.beneficiariesChanged && formData?.beneficiariesNumberChange && formData?.environmentMeasuresChange) return;
            const data = {
                contentId: ID,
                totalDirect: formData?.beneficiariesNumberChange === "Y" ? formData?.totalDirect : directBeneficiaries,
                totalIndirect: formData?.beneficiariesNumberChange === "Y" ? formData?.totalIndirect : indirectBeneficiaries,
                actualEnvironmentalMeasure: formData?.environmentMeasuresChange === "Y" ? formData?.actualEnvironmentalMeasure : (environmentalMeasure || environmentMeasure),
                beneficiariesType: formData?.beneficiariesChanged === "Y" ? formData?.beneficiariesType : opportunityBeneficiaries,
                beneficiariesChanged: formData?.beneficiariesChanged,
                beneficiariesNumberChange: formData?.beneficiariesNumberChange,
                environmentMeasuresChange: formData?.environmentMeasuresChange,
                action: "Draft"
            }
            dispatch(editContent(data));
            dispatch(toggleSpinner(true));
        });
    }

    const handleSaveAndExit = () => {
        setSavedClicked(true);
        setTimeout(() => {
            form.submit();
        }, 300);
    }

    const handlePrevious = () => {
        navigate(`/impact-maker/content/final/outputs/${ID}`)
    }

    const onFormChange = (_, allValues) => {
        setFormData(allValues);
    };

    const { beneficiariesChanged: formBeneficiariesChanged, beneficiariesNumberChange: formBeneficiariesNumberChange, environmentMeasuresChange: formEnvironmentMeasuresChange } = formData || {};

    return (
        <div className={baseClassName}>
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <Steps
                current={1}
                items={[
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_1" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "inputs_n_outputs" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_2" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3c" className='mainTitle'>
                            {intl.formatMessage({ id: "objectives_n_outcomes" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_3" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "case_studies" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_4" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "next_steps" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_5" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "preview_n_submit" })}
                        </StyledText>,
                    },
                ]}
            />
            <div className='contentContainer'>
                <div>
                    <StyledText as="p" variant="H1" className='mainTitle'>
                        {intl.formatMessage({ id: "beneficiaries" })}
                    </StyledText>
                    <Form
                        {...formItemLayout}
                        labelAlign="left"
                        onFinish={handleProceed}
                        onFinishFailed={onFinishFailed}
                        form={form}
                        className="formContainer"
                        scrollToFirstError
                        onValuesChange={onFormChange}
                    >
                        <div className="mainContentContainer">
                            <div className="contentContainer">
                                <StyledText as="p" variant="B3">
                                    {intl.formatMessage({ id: "below_are_the_beneficiaries_you_shared_in_your_application" })}
                                </StyledText>
                                <div className="flexBox justifyContent">
                                    {Array.isArray(opportunityBeneficiaries) && opportunityBeneficiaries?.length > 0 && <div className="whiteSupportContainer volunteeringContainer">
                                        <div className="titleContainer">
                                            <div><Avatar src={socialIcon} /></div>
                                            <div>
                                                <StyledText as="p" variant="H3">
                                                    {intl.formatMessage({ id: "social_beneficiaries" })}
                                                </StyledText>
                                            </div>
                                        </div>
                                        <ul>{opportunityBeneficiaries.map((el) => <li key={el}><StyledText as="span" variant="B3">{el}</StyledText></li>)}</ul>
                                        <HorizontalGraph
                                            name={intl.formatMessage({
                                                id: 'direct',
                                            })}
                                            percentage={Number(
                                                directBeneficiaries
                                            )}
                                            maxValue={Math.max(Number(
                                                directBeneficiaries
                                            ), Number(
                                                indirectBeneficiaries
                                            ))}
                                            background="#56D9FE"
                                        />

                                        <HorizontalGraph
                                            name={intl.formatMessage({
                                                id: 'indirect',
                                            })}
                                            percentage={Number(
                                                indirectBeneficiaries
                                            )}
                                            maxValue={Math.max(Number(
                                                directBeneficiaries
                                            ), Number(
                                                indirectBeneficiaries
                                            ))}
                                            background="#A4A1FB"
                                        />
                                    </div>}
                                    {(environmentMeasure)?.length > 0 && <div className="whiteSupportContainer volunteeringContainer">
                                        <div className="titleContainer">
                                            <div><Avatar src={environmentIcon} /></div>
                                            <div>
                                                <StyledText as="p" variant="H3">
                                                    {intl.formatMessage({ id: "environmental_beneficiaries" })}
                                                </StyledText>
                                            </div>
                                        </div>
                                        <ul>{(environmentMeasure)?.map((el) => <li key={el}><StyledText as="span" variant="B3"><span className="outputNumber">{el?.amount}</span>{` ${el?.unit} ${el?.unit === 'Hectares' ? 'of ' : ''}${el?.subCategory} ${el?.category === 'Habitats' ? `${intl.formatMessage({ id: 'habitats' })} ` : ''} ${el?.description}`}</StyledText></li>)}</ul>
                                    </div>}
                                </div>
                                <StyledText as="p" variant="B3d">
                                    {intl.formatMessage({ id: "did_your_social_beneficiary_types_change" })}
                                </StyledText>
                                <Form.Item
                                    name="beneficiariesChanged"
                                    rules={[
                                        { required: true, message: intl.formatMessage({ id: "enter" }) }
                                    ]}
                                >
                                    <Radio.Group className="radioGroup">
                                        <Radio className="radio" value="Y">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "yes" })}
                                            </StyledText>
                                        </Radio>
                                        <Radio className="radio" value="N">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "no" })}
                                            </StyledText>
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                                {formBeneficiariesChanged === "Y" && <>
                                    <StyledText as="p" variant="B3d">
                                        {intl.formatMessage({ id: "please_update_your_social_beneficiary_types" })}
                                    </StyledText>
                                    <Form.Item
                                        name="beneficiariesType"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please select',
                                            },
                                        ]}
                                    >
                                        <Select
                                            allowClear
                                            placeholder={intl.formatMessage({ id: "select_title" })}
                                            type="formSelect"
                                            mode="multiple"
                                            options={beneficiariesList.map((value, index) => ({
                                                key: value,
                                                label: value,
                                                value: value
                                            }))
                                            }
                                        />
                                    </Form.Item>
                                </>}
                                <StyledText as="p" variant="B3d">
                                    {intl.formatMessage({ id: "did_your_social_beneficiary_numbers_change" })}
                                </StyledText>
                                <Form.Item
                                    name="beneficiariesNumberChange"
                                    rules={[
                                        { required: true, message: intl.formatMessage({ id: "enter" }) }
                                    ]}
                                >
                                    <Radio.Group className="radioGroup">
                                        <Radio className="radio" value="Y">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "yes" })}
                                            </StyledText>
                                        </Radio>
                                        <Radio className="radio" value="N">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "no" })}
                                            </StyledText>
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                                {formBeneficiariesNumberChange === "Y" && <>
                                    <StyledText as="p" variant="B3d">
                                        {intl.formatMessage({ id: "please_update_your_social_beneficiary_types" })}
                                    </StyledText>
                                    <Form.Item
                                        name="totalDirect"
                                        className="formItem smallInput"
                                        rules={[
                                            {
                                                whitespace: true,
                                                required: true,
                                                message: intl.formatMessage({ id: "please_enter" }),
                                            },

                                            { validator: numericValidator, message: intl.formatMessage({ id: 'please_enter_numeric_value' }) }
                                        ]}
                                    >
                                        <Input
                                            placeholder={intl.formatMessage({ id: "enter_estimated_number" })}
                                            type="text"
                                        />
                                    </Form.Item>
                                    <StyledText as="p" variant="B3d">
                                        {intl.formatMessage({ id: "indirect_beneficiaries" })}
                                        <StyledText as="span" variant="B3a">
                                            {` (${intl.formatMessage({ id: "optional" })})`}
                                        </StyledText>
                                    </StyledText>
                                    <Form.Item
                                        name="totalIndirect"
                                        className="formItem smallInput"
                                        rules={[
                                            { validator: numericValidator, message: intl.formatMessage({ id: 'please_enter_numeric_value' }) }
                                        ]}
                                    >
                                        <Input
                                            placeholder={intl.formatMessage({ id: "enter_estimated_number" })}
                                            type="text"
                                        />
                                    </Form.Item>
                                </>}
                                <StyledText as="p" variant="B3d">
                                    {intl.formatMessage({ id: "did_your_environmental_beneficiaries_change" })}
                                </StyledText>
                                <Form.Item
                                    name="environmentMeasuresChange"
                                    rules={[
                                        { required: true, message: intl.formatMessage({ id: "enter" }) }
                                    ]}
                                >
                                    <Radio.Group className="radioGroup">
                                        <Radio className="radio" value="Y">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "yes" })}
                                            </StyledText>
                                        </Radio>
                                        <Radio className="radio" value="N">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "no" })}
                                            </StyledText>
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                                {formEnvironmentMeasuresChange === "Y" && <Form.List name="actualEnvironmentalMeasure">
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map(({ key, name }, index) => (
                                                <Space key={key} style={{ display: 'flex', marginBottom: 0, flexWrap: 'wrap' }} className='optionsContainer'>
                                                    <Form.Item label="" name={[name, 'category']} rules={[
                                                        {
                                                            whitespace: true,
                                                            required: true,
                                                            message: intl.formatMessage({ id: "please_select" }),
                                                        },
                                                    ]}>
                                                        <Select
                                                            allowClear
                                                            placeholder={intl.formatMessage({ id: "select_type_title" })}
                                                            type="formSelect"
                                                            options={categories.map((value) => ({
                                                                key: value?.category,
                                                                label: value?.category,
                                                                value: value?.category
                                                            }))
                                                            }
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="" name={[name, 'subCategory']} rules={[
                                                        {
                                                            whitespace: true,
                                                            required: true,
                                                            message: intl.formatMessage({ id: "please_select" }),
                                                        },
                                                    ]}>
                                                        <Select
                                                            allowClear
                                                            placeholder={intl.formatMessage({ id: "select_sub_type" })}
                                                            type="formSelect"
                                                            options={categories
                                                                ?.find(
                                                                    (item) => item.category
                                                                        === formData?.actualEnvironmentalMeasure?.[index]?.category)
                                                                ?.subCategory?.map((el) => ({
                                                                    key: el,
                                                                    label: el,
                                                                    value: el
                                                                }))}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="" name={[name, 'description']} rules={[
                                                        {
                                                            whitespace: true,
                                                            required: true,
                                                            message: intl.formatMessage({ id: "please_select" }),
                                                        },
                                                    ]}>
                                                        <Select
                                                            allowClear
                                                            placeholder={intl.formatMessage({ id: "select_action" })}
                                                            type="formSelect"
                                                            options={categories
                                                                ?.find(
                                                                    (item) => item.category
                                                                        === formData?.actualEnvironmentalMeasure?.[index]?.category)
                                                                ?.actions?.map((el) => ({
                                                                    key: el,
                                                                    label: el,
                                                                    value: el
                                                                }))}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item className='inputElement' name={[name, 'amount']} rules={[
                                                        {
                                                            whitespace: true,
                                                            required: true,
                                                            message: intl.formatMessage({ id: "please_enter" }),
                                                        },
                                                        { validator: numericValidator, message: intl.formatMessage({ id: 'please_enter_numeric_value' }) }
                                                    ]}>
                                                        <Input
                                                            placeholder={intl.formatMessage({ id: "enter_amount" })}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="" name={[name, 'unit']} rules={[
                                                        {
                                                            whitespace: true,
                                                            required: true,
                                                            message: intl.formatMessage({ id: "please_select" }),
                                                        },
                                                    ]}>
                                                        <Select
                                                            allowClear
                                                            placeholder={intl.formatMessage({ id: "select_units" })}
                                                            type="formSelect"

                                                            options={categories
                                                                ?.find(
                                                                    (item) => item.category
                                                                        === formData?.actualEnvironmentalMeasure?.[index]?.category)
                                                                ?.units?.map((el) => ({
                                                                    key: el,
                                                                    label: el,
                                                                    value: el
                                                                }))}
                                                        />
                                                    </Form.Item>
                                                    <MinusCircleOutlined onClick={() => remove(name)} style={{ fontSize: '20px', color: '#04ac9c' }} className='for' />
                                                </Space>
                                            ))}
                                            {formListError && (
                                                <StyledText as="p" variant="B3" className="errorText">
                                                    {formListError}
                                                </StyledText>
                                            )}
                                            <Form.Item>
                                                <AntdButton htmlType='button' className='addButton' type="dashed" onClick={() => add()} block icon={<PlusOutlined fill="red" />}>

                                                    <StyledText as="p" variant="B3">
                                                        {intl.formatMessage({ id: "add" })}
                                                    </StyledText>
                                                </AntdButton>
                                            </Form.Item>
                                            {formData.actualEnvironmentalMeasure?.map((el) => (
                                                el?.amount && el?.unit && el?.subCategory && el?.category && el?.description ? (
                                                    <StyledText as="p" key={el?.amount} variant="B3b">
                                                        {`${el.amount} ${(el.category === 'Habitats' || el.category === 'Species') && el.unit !== 'Number' ? el.unit : ''} ${el.unit === 'Hectares' ? 'of ' : ''}${el.subCategory} ${el.category === 'Habitats' ? `${intl.formatMessage({ id: 'habitats' })} ` : ''} ${el.description}`}
                                                    </StyledText>
                                                ) : null
                                            ))}
                                        </>
                                    )}
                                </Form.List>}
                                <div className='buttonContainer'>
                                    <Divider />
                                    <div className='buttonContentContainer'>
                                        <div className='nextAndPreviousContainer'>
                                            <Button
                                                variant="secondary"
                                                type="button"
                                                onClick={() => handlePrevious()}>
                                                {intl.formatMessage({ id: "previous" })}
                                            </Button>
                                            <Button
                                                variant="primary"
                                                htmlType="submit"
                                            >
                                                {intl.formatMessage({ id: "next" })}
                                            </Button>
                                        </div>
                                        <div className="saveAndExitButton">
                                            <Button
                                                variant="secondary"
                                                htmlType="button"
                                                onClick={() => handleSaveAndExit()}>
                                                {intl.formatMessage({ id: "save_n_exit" })}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="buttonContainer">
                                <Divider />
                                <div className="buttonContentContainer">
                                    <div className="nextAndPreviousContainer">
                                        <Button variant="secondary" type="button" onClick={() => handlePrevious()}>
                                            {intl.formatMessage({ id: "previous" })}
                                        </Button>
                                        <Button variant="primary" htmlType="submit">
                                            {intl.formatMessage({ id: "next" })}
                                        </Button>
                                    </div>
                                    <div className="saveAndExitButton">
                                        <Button variant="secondary" htmlType="button" onClick={() => handleSaveAndExit()}>
                                            {intl.formatMessage({ id: "save_n_exit" })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    )
}

const StyledCharityInterimCreateReport = styled(CharityInterimCreateReport)`
  &.charityInterimCreateReport {
    background: white;
    display: flex;
    flex-direction: column;
    padding: 20px;
    justify-content: space-between;
    margin-top: 50px;
    .inputElement .ant-form-item-control-input{
        width: 200px;
    }

    .whiteSupportContainer{
        background: white;
        box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          width: 35vw;
          &.volunteeringContainer {
         box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
    }

    .smallInput{
        width: 500px;
    }

    .flexBox{
        display: flex;
        align-items: flex-start;
        gap: 20px;
        &.justifyContent{
            justify-content: flex-start;
        }
        .flexBoxChild{
            display: flex;
            flex-direction: column;
            gap: 10px;
            .flexColumn{
                display: flex;
                flex-direction: column;
                .noBreak{
                    white-space: nowrap;
                }
            }
        }
    }

    .mainTitle {
      margin-bottom: 20px;
    }

    .contentContainer {
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: 60vh;
      padding-bottom: 60px;

      .formContainer {
        display: flex;
        gap: 12px;
        flex-direction: column;
      }
    }

    .overlay {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1000;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.45);
    }

    .spinner {
      z-index: 2000;
      margin: auto;
      left: 50%;
      right: 50%;
      position: absolute;
      bottom: 50%;
    }

    .radioGroup {
      display: flex;
      flex-direction: column;
    }

    .formItemTextField {
      .ant-form-item-control-input {
        width: 34.5vw !important;
      }
    }

    .formItemTextFieldPart {
      .ant-form-item-control-input {
        width: 16vw !important;
      }
    }

    .helperText {
      color: #5271FF !important;
      font-weight: bold !important;
      margin-bottom: 0px !important;
    }

    .ant-form-item {
      margin-bottom: 10px;
    }

    .formItemInput {
      width: 300px;
    }

    .mainTitle{
        margin-bottom: 20px;
    }
    .errorText{
        color: #ff4d4f;
    }
    .contentContainer{
        display: flex;
    }
    .addButton{
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }
    .formContainer{
        display: flex;
        gap: 12px;
        flex-direction: column;
    }
    .optionsContainer{
        display: flex;
        align-items: center;
        gap: 30px;
        .for{
            margin-bottom: 10px;
        }
    }

    .radioGroup {
            display: flex;
            flex-direction: column;
    }

    .buttonContainer{
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -110px;
        .buttonContentContainer{
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton{
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer{
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }
    .formItemTextField{
        .ant-form-item-control-input{
            width: 34.5vw !important;
        }
    }
    .formItemTextFieldPart{
        .ant-form-item-control-input{
            width: 16vw !important;
        } 
    }
    .helperText {
    color: #5271FF !important;
    font-weight: bold !important;
    margin-bottom: 0px !important;
  }
  .ant-form-item{
    margin-bottom: 10px;
  }
  .formItemInput{
    width: 300px;
  }
  }
`;

export default StyledCharityInterimCreateReport;
